import React, { useState } from 'react';
import SEO from '../../components/ui/SEO';
import { ChevronDown, ChevronUp, Shield, Lock, FileText, AlertCircle, Eye, Users, Mail } from 'lucide-react';
export default function Privacy() {
  const [openSections, setOpenSections] = useState({
    introduction: true,
    dataCollection: true,
    dataSharing: true,
    cookies: true,
    security: true,
    international: true,
    rights: true,
    complaints: true,
    changes: true,
    contact: true,
    schedule: true
  });
  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <main className="min-h-screen bg-white" data-id="element-614" data-mp="3lrlt1spkoa22">
      <SEO title="Privacy and Cookie Policy | Medicolegal Psychiatry" description="How Medicolegal Psychiatry collects, processes, stores and protects personal information under UK GDPR. Contact us with any data protection query." path="/policies/privacy" keywords={['privacy policy', 'cookie policy', 'expert witness data protection']} imageAlt="Privacy and cookie policy for Medicolegal Psychiatry" data-id="element-615" data-mp="3qnsnopcipz4p" />
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-616" data-mp="16d6ulgi5176b">
        <div className="container mx-auto px-4" data-id="element-617" data-mp="11evlx93kx2po">
          <div className="flex items-center justify-center mb-4" data-id="element-618" data-mp="11g6y9xb3bise">
            <Shield className="h-12 w-12 mr-4" data-id="element-619" data-mp="dqvvkaa6jebm" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-620" data-mp="drnrjm7ove2f">
              Privacy and Cookie Policy
            </h1>
          </div>
          <p className="text-lg max-w-3xl mx-auto text-center" data-id="element-621" data-mp="mrkcpduvdusq">
            Last Updated: September 12, 2025
          </p>
        </div>
      </section>
      {/* Policy Content */}
      <section className="py-12" data-id="element-622" data-mp="1y4wi3t6a2lh0">
        <div className="container mx-auto px-4" data-id="element-623" data-mp="1y4388jc0vfun">
          <div className="max-w-3xl mx-auto" data-id="element-624" data-mp="1u4cj0k2597s8">
            <p className="text-slate-dark text-lg mb-8" data-id="element-625" data-mp="1u3j9xcfpq66x">
              This Privacy and Cookie Policy explains how Medicolegal Psychiatry
              (referred to as "we," "us," or "our"), operated by Dr. Imtiaz
              Syed, collects, uses, processes, and protects your personal
              information when you use our services, visit our website
              (medicolegal-psychiatry.co.uk), or otherwise interact with us.
            </p>
            <div className="space-y-4 mb-8" data-id="element-626" data-mp="82xg6jn8igcp">
              {/* Introduction Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-627" data-mp="d1s2vwobop23">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.introduction ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('introduction')} data-id="element-628" data-mp="cze5via312rw">
                  <div className="flex items-center" data-id="element-629" data-mp="3lhbrgie1tjat">
                    <FileText className="h-5 w-5 mr-2" data-id="element-630" data-mp="3pidrh1fshzdu" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-631" data-mp="3pgsvr8hqoaee">
                      1. Introduction
                    </h2>
                  </div>
                  {openSections.introduction ? <ChevronUp className="h-5 w-5" data-id="element-632" data-mp="3cyqkyl780b5j" /> : <ChevronDown className="h-5 w-5" data-id="element-633" data-mp="38x4zibgmeojq" />}
                </button>
                {openSections.introduction && <div className="p-6 bg-white" data-id="element-634" data-mp="2z1bpayqdj754">
                    <p className="text-slate-dark mb-4" data-id="element-635" data-mp="2z2397calzgfk">
                      Welcome to Medicolegal Psychiatry. We are committed to
                      upholding the highest standards of privacy and data
                      protection in all our operations. This policy outlines our
                      practices for handling personal data, ensuring compliance
                      with the UK General Data Protection Regulation (UK GDPR),
                      the Data Protection Act 2018 (DPA 2018), and other
                      relevant legislation.
                    </p>
                  </div>}
              </div>
              {/* Data Collection Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-636" data-mp="k6mgld8l04wz">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataCollection ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataCollection')} data-id="element-637" data-mp="5hgm22kov2vf">
                  <div className="flex items-center" data-id="element-638" data-mp="k82pynt0ssl">
                    <Eye className="h-5 w-5 mr-2" data-id="element-639" data-mp="gu1itqvmpmq" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-640" data-mp="3ipte78u5czen">
                      2. How We Collect and Use Your Information
                    </h2>
                  </div>
                  {openSections.dataCollection ? <ChevronUp className="h-5 w-5" data-id="element-641" data-mp="1gvc0ta7ol9m4" /> : <ChevronDown className="h-5 w-5" data-id="element-642" data-mp="1lvzqnsk6bker" />}
                </button>
                {openSections.dataCollection && <div className="p-6 bg-white" data-id="element-643" data-mp="31fpjqek4s75q">
                    <p className="text-slate-dark mb-4" data-id="element-644" data-mp="31h0mqc5r5avs">
                      We collect and process personal data primarily to provide
                      our medico-legal psychiatric assessments and expert
                      witness services. This includes:
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-645" data-mp="3p3qmt7yjivdr">
                      a) Personal Information We Collect:
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-646" data-mp="2xjyqrv8c77w">
                      <li className="mb-2" data-id="element-647" data-mp="2vr0o0yomvbi">
                        Contact Details: Your name, title, professional address,
                        email address, and phone number.
                      </li>
                      <li className="mb-2" data-id="element-648" data-mp="24r1eyjxfege9">
                        Client Information: If you are an instructing party, we
                        will process personal data relating to your client,
                        including their name, contact details, date of birth,
                        photographic identification, and special category data
                        such as medical records, mental health history, and case
                        documentation.
                      </li>
                      <li className="mb-2" data-id="element-649" data-mp="38d3i3oc1isuk">
                        Account Information: For our clients, this may include
                        usernames, passwords, security questions, and
                        preferences.
                      </li>
                      <li className="mb-2" data-id="element-650" data-mp="3h6qdveg8zw3r">
                        Financial Information: Payment details, transaction
                        history, and billing information.
                      </li>
                      <li className="mb-2" data-id="element-651" data-mp="3r564lgbd53cq">
                        Communications: Records of your interactions with us via
                        email, phone, or post.
                      </li>
                      <li className="mb-2" data-id="element-652" data-mp="1kgxbrqfacxej">
                        Website Usage Data: Information collected through
                        cookies and similar technologies when you visit our
                        website.
                      </li>
                      <li data-id="element-653" data-mp="15ibrg2ucbtku">
                        Professional Details: Information about your
                        professional standing, such as SRA registration status
                        and professional memberships.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-654" data-mp="1tg0x711xdow7">
                      b) How We Use Your Information:
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-655" data-mp="1og654u6eyt9k">
                      <li className="mb-2" data-id="element-656" data-mp="2lwv1w9gib5c3">
                        Service Provision: To conduct assessments, prepare
                        medico-legal reports, provide expert witness testimony,
                        and manage appointments.
                      </li>
                      <li className="mb-2" data-id="element-657" data-mp="1u8qr7rqo44bp">
                        Contractual Obligations: To manage our relationship with
                        you, process invoices, and receive payments.
                      </li>
                      <li className="mb-2" data-id="element-658" data-mp="1lfx8fkc0imt3">
                        Legal & Regulatory Compliance: To comply with legal
                        obligations, professional requirements, and respond to
                        lawful requests from courts, tribunals, or regulatory
                        bodies.
                      </li>
                      <li className="mb-2" data-id="element-659" data-mp="1z7bqnoy3ijz3">
                        Communication: To respond to your enquiries, provide
                        updates on your case, and send important information.
                      </li>
                      <li className="mb-2" data-id="element-660" data-mp="nv178958s07b">
                        Business Improvement: To analyse website usage, improve
                        our services, and maintain security.
                      </li>
                      <li data-id="element-661" data-mp="g95rukphtu25">
                        Risk Management: To ensure the safety of our staff
                        during visits.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-662" data-mp="u6kjlw37ydxa">
                      c) Legal Grounds for Processing:
                    </h3>
                    <p className="text-slate-dark mb-2" data-id="element-663" data-mp="1l2jj4nza6a6">
                      We process your personal data based on the following
                      lawful bases:
                    </p>
                    <ul className="list-disc pl-6 text-slate-dark" data-id="element-664" data-mp="bb477vcf5mok">
                      <li className="mb-2" data-id="element-665" data-mp="6jm7fam4ts56">
                        Performance of a Contract: Necessary for providing our
                        medico-legal services as per your instruction.
                      </li>
                      <li className="mb-2" data-id="element-666" data-mp="3a0uklh5x03tk">
                        Compliance with a Legal Obligation: To meet our duties
                        as a healthcare professional and expert witness, and for
                        regulatory compliance.
                      </li>
                      <li className="mb-2" data-id="element-667" data-mp="2a800gz7jpqeq">
                        Legitimate Interests: To manage our business operations,
                        improve services, ensure security, and for effective
                        communication.
                      </li>
                      <li className="mb-2" data-id="element-668" data-mp="219oamzei6wnc">
                        Consent: For processing special category data where
                        explicit consent is obtained, and for specific marketing
                        communications.
                      </li>
                      <li data-id="element-669" data-mp="1mk08xx2etvz3">
                        Vital Interests: In rare cases, to protect the vital
                        interests of an individual.
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Data Sharing Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-670" data-mp="3p4uhul7fh1ek">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataSharing ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataSharing')} data-id="element-671" data-mp="3u3e8gywyv1d9">
                  <div className="flex items-center" data-id="element-672" data-mp="36fedtnwjaqte">
                    <Users className="h-5 w-5 mr-2" data-id="element-673" data-mp="36crz94b87b1o" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-674" data-mp="3afoj0yci5d77">
                      3. Data Sharing
                    </h2>
                  </div>
                  {openSections.dataSharing ? <ChevronUp className="h-5 w-5" data-id="element-675" data-mp="3k7b4romguuo7" /> : <ChevronDown className="h-5 w-5" data-id="element-676" data-mp="2mrf50ilj5tqm" />}
                </button>
                {openSections.dataSharing && <div className="p-6 bg-white" data-id="element-677" data-mp="2rodugnu91cih">
                    <p className="text-slate-dark mb-4" data-id="element-678" data-mp="3v0maoq58us6x">
                      We may share your personal information in the following
                      circumstances:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-679" data-mp="7nk0vc21u2vi">
                      <li className="mb-2" data-id="element-680" data-mp="7pmppsrgtpyn">
                        With You: As the Data Controller, we will share
                        information with you as required by our contract and
                        your instructions.
                      </li>
                      <li className="mb-2" data-id="element-681" data-mp="3748iukktjl0e">
                        With Your Clients: If you are the instructing party, we
                        will share relevant information with your client as
                        necessary for the assessment and reporting process.
                      </li>
                      <li className="mb-2" data-id="element-682" data-mp="10b67kt6kpnml">
                        With Other Experts: If required for a joint report or to
                        obtain further information relevant to the case.
                      </li>
                      <li className="mb-2" data-id="element-683" data-mp="13whq7f5nus65">
                        With Courts, Tribunals, and Regulatory Bodies: As
                        legally mandated.
                      </li>
                      <li className="mb-2" data-id="element-684" data-mp="ty1wd4f914sy">
                        With Third-Party Service Providers: For IT support,
                        secure cloud storage, and payment processing, all bound
                        by strict confidentiality agreements.
                      </li>
                      <li className="mb-2" data-id="element-685" data-mp="1rjg1zziu5bxq">
                        For Safety: If necessary, to ensure the safety of our
                        staff during site visits, we may arrange for an
                        accompanying person.
                      </li>
                      <li data-id="element-686" data-mp="25jh23cra0fc5">
                        With Legal or Regulatory Authorities: If required by law
                        or in response to valid legal process.
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-687" data-mp="lpfmzeu7oklx">
                      We do not sell your personal information.
                    </p>
                  </div>}
              </div>
              {/* Cookies Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-688" data-mp="cpsvplv74v6l">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.cookies ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('cookies')} data-id="element-689" data-mp="7qpmrqc3i4tc">
                  <div className="flex items-center" data-id="element-690" data-mp="vlsgocpraygo">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-691" data-mp="qnquhix1aj0z" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-692" data-mp="qn7mov13lb0o">
                      4. Cookies and Website Usage
                    </h2>
                  </div>
                  {openSections.cookies ? <ChevronUp className="h-5 w-5" data-id="element-693" data-mp="1jb3fvprgkksp" /> : <ChevronDown className="h-5 w-5" data-id="element-694" data-mp="1qoulbzgvjnd7" />}
                </button>
                {openSections.cookies && <div className="p-6 bg-white" data-id="element-695" data-mp="20lqmfk4g177q">
                    <p className="text-slate-dark mb-4" data-id="element-696" data-mp="20kxnv6fn1o0o">
                      Our website, medicolegal-psychiatry.co.uk, uses cookies to
                      enhance your experience and improve our services.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-697" data-mp="29kccs1g5pqj2">
                      What are Cookies? Cookies are small text files placed on
                      your device when you visit a website. They help us
                      understand how visitors interact with our site, which
                      pages are popular, and how we can improve.
                    </p>
                    <p className="text-slate-dark mb-2" data-id="element-698" data-mp="2tbz8hn23obrl">
                      Types of Cookies Used:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-699" data-mp="2x9wukh0kqhqr">
                      <li className="mb-2" data-id="element-700" data-mp="y7regbhansfw">
                        Strictly Necessary Cookies: Essential for the website to
                        function correctly.
                      </li>
                      <li className="mb-2" data-id="element-701" data-mp="1714gq41906en">
                        Performance Cookies: Collect anonymous data on how
                        visitors use our site.
                      </li>
                      <li data-id="element-702" data-mp="1gwfvgmcnpdme">
                        Functionality Cookies: Remember choices you make to
                        provide a more personalised experience.
                      </li>
                    </ul>
                    <p className="text-slate-dark mb-4" data-id="element-703" data-mp="1pxmgn8q93clz">
                      Managing Cookies: You can manage your cookie preferences
                      through your browser settings. Most browsers allow you to
                      block or delete cookies. However, disabling cookies may
                      affect some website functionalities.
                    </p>
                    <p className="text-slate-dark" data-id="element-704" data-mp="3dljret74aua4">
                      Analytics: We may use tools like Google Analytics to
                      understand website traffic and user behaviour.
                    </p>
                  </div>}
              </div>
              {/* Security Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-705" data-mp="38rpb7zqq0xfs">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.security ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('security')} data-id="element-706" data-mp="2tvhtr76p6a47">
                  <div className="flex items-center" data-id="element-707" data-mp="2ptw7iv89wjhc">
                    <Lock className="h-5 w-5 mr-2" data-id="element-708" data-mp="2pws86rbi9m9h" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-709" data-mp="j59in61oqs4u">
                      5. Data Security and Retention
                    </h2>
                  </div>
                  {openSections.security ? <ChevronUp className="h-5 w-5" data-id="element-710" data-mp="1wmf8v26yqk3j" /> : <ChevronDown className="h-5 w-5" data-id="element-711" data-mp="1rodngam2e8ow" />}
                </button>
                {openSections.security && <div className="p-6 bg-white" data-id="element-712" data-mp="1mw3zv18f9kps">
                    <p className="text-slate-dark mb-4" data-id="element-713" data-mp="1iwv79zp4bsvt">
                      We implement robust technical and organisational measures
                      to protect your personal data against unauthorised access,
                      loss, alteration, or disclosure. This includes secure data
                      storage, access controls, and regular security
                      assessments.
                    </p>
                    <p className="text-slate-dark" data-id="element-714" data-mp="zysoi2n5ys6g">
                      We retain personal data only for as long as necessary to
                      fulfil the purposes outlined in this policy, comply with
                      legal and regulatory obligations, resolve disputes, and
                      enforce our agreements. Retention periods are based on
                      legal requirements (e.g., medical record retention) and
                      operational needs.
                    </p>
                  </div>}
              </div>
              {/* Additional sections follow the same pattern */}
              {/* International Data Transfers */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-715" data-mp="3tbb1a7r6y7sq">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.international ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('international')} data-id="element-716" data-mp="23gdc260rxqn">
                  <div className="flex items-center" data-id="element-717" data-mp="1hkyxgru9fimv">
                    <FileText className="h-5 w-5 mr-2" data-id="element-718" data-mp="1hk74los1rz0a" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-719" data-mp="1mja5qy4wpl2z">
                      6. International Data Transfers
                    </h2>
                  </div>
                  {openSections.international ? <ChevronUp className="h-5 w-5" data-id="element-720" data-mp="1sykk8uwc58te" /> : <ChevronDown className="h-5 w-5" data-id="element-721" data-mp="1xpsu1s1kuw73" />}
                </button>
                {openSections.international && <div className="p-6 bg-white" data-id="element-722" data-mp="22onjvdxypl0p">
                    <p className="text-slate-dark" data-id="element-723" data-mp="1526ncax1zbg0">
                      Your personal information will primarily be processed
                      within the UK. If data is transferred outside the UK or
                      European Economic Area (EEA), we will ensure that
                      appropriate safeguards are in place, such as Standard
                      Contractual Clauses, to protect your data in accordance
                      with applicable laws.
                    </p>
                  </div>}
              </div>
              {/* Your Rights */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-724" data-mp="jr94yn9lh4e7">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.rights ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('rights')} data-id="element-725" data-mp="ero427av6lyu">
                  <div className="flex items-center" data-id="element-726" data-mp="51l57qh2adsl">
                    <Shield className="h-5 w-5 mr-2" data-id="element-727" data-mp="30d6ebyr0f8" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-728" data-mp="npdkjixvu9">
                      7. Your Rights
                    </h2>
                  </div>
                  {openSections.rights ? <ChevronUp className="h-5 w-5" data-id="element-729" data-mp="3n9kbd8e69ulo" /> : <ChevronDown className="h-5 w-5" data-id="element-730" data-mp="3i9xwkuip46ar" />}
                </button>
                {openSections.rights && <div className="p-6 bg-white" data-id="element-731" data-mp="1gh08td3s03wo">
                    <p className="text-slate-dark mb-4" data-id="element-732" data-mp="1gibddf51pfou">
                      As data subjects, you have several rights regarding your
                      personal data:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-733" data-mp="3foqgsg58rpbf">
                      <li className="mb-2" data-id="element-734" data-mp="30zu05up4d3z7">
                        Right to Access: Request a copy of the personal data we
                        hold about you.
                      </li>
                      <li className="mb-2" data-id="element-735" data-mp="3tn6oa9axhjum">
                        Right to Rectification: Request correction of inaccurate
                        or incomplete personal data.
                      </li>
                      <li className="mb-2" data-id="element-736" data-mp="3jq21q8d67mda">
                        Right to Erasure (Right to be Forgotten): Request
                        deletion of your personal data under certain conditions.
                      </li>
                      <li className="mb-2" data-id="element-737" data-mp="289ls4voarzyc">
                        Right to Restriction of Processing: Request that we
                        limit the way we use your personal data.
                      </li>
                      <li className="mb-2" data-id="element-738" data-mp="vaa7cx8f7efl">
                        Right to Data Portability: Request a transfer of your
                        personal data to you or another controller in a
                        structured, commonly used, and machine-readable format.
                      </li>
                      <li className="mb-2" data-id="element-739" data-mp="10bpq2in4l8ut">
                        Right to Object: Object to processing based on
                        legitimate interests or for direct marketing purposes.
                      </li>
                      <li className="mb-2" data-id="element-740" data-mp="7kfrs7wpvbrn">
                        Rights Related to Automated Decision-Making and
                        Profiling: You have the right not to be subject to a
                        decision based solely on automated processing.
                      </li>
                      <li data-id="element-741" data-mp="3p0nv5qjnuaeb">
                        Right to Withdraw Consent: If we rely on your consent
                        for processing, you have the right to withdraw it at any
                        time.
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-742" data-mp="6kv3a2k02w2g">
                      Exercising Your Rights: To exercise any of your rights,
                      please contact us in writing using the details provided
                      below. We may ask for proof of identity to verify your
                      request. We aim to respond to all legitimate requests
                      within one month. If your request is complex or numerous,
                      we may extend this period by two months.
                    </p>
                  </div>}
              </div>
              {/* Complaints */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-743" data-mp="z7zdclxya0l3">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.complaints ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('complaints')} data-id="element-744" data-mp="2w2c7wnw1cjk8">
                  <div className="flex items-center" data-id="element-745" data-mp="2opmf2n3aabjk">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-746" data-mp="2oo1n958ct6pe" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-747" data-mp="2jrl12gkpt2ey">
                      8. Complaints
                    </h2>
                  </div>
                  {openSections.complaints ? <ChevronUp className="h-5 w-5" data-id="element-748" data-mp="3ce5rnetd9ahg" /> : <ChevronDown className="h-5 w-5" data-id="element-749" data-mp="37g470pgal33v" />}
                </button>
                {openSections.complaints && <div className="p-6 bg-white" data-id="element-750" data-mp="32fg9rm1gzq17">
                    <p className="text-slate-dark mb-4" data-id="element-751" data-mp="2xiziwk2vyxkf">
                      If you are unhappy with how we have handled your personal
                      data or any aspect of our privacy practices, you have the
                      right to lodge a complaint with the Information
                      Commissioner's Office (ICO), the UK's supervisory
                      authority for data protection issues.
                    </p>
                    <p className="text-slate-dark mb-2" data-id="element-752" data-mp="i4e6znkciaim">
                      ICO Website: www.ico.org.uk
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-753" data-mp="94ir55fuhrc9">
                      ICO Helpline: 0303 123 1113
                    </p>
                    <p className="text-slate-dark" data-id="element-754" data-mp="3qkqz71dqhe59">
                      We would appreciate the opportunity to address your
                      concerns directly before you approach the ICO. Please
                      contact us first.
                    </p>
                  </div>}
              </div>
              {/* Changes to Policy */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-755" data-mp="37m4sn6zg6uty">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.changes ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('changes')} data-id="element-756" data-mp="37o7r8feo1waa">
                  <div className="flex items-center" data-id="element-757" data-mp="2ozf64w416y4w">
                    <FileText className="h-5 w-5 mr-2" data-id="element-758" data-mp="32xc2psihj4jk" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-759" data-mp="32yx1jubu19n8">
                      9. Changes to This Privacy and Cookie Policy
                    </h2>
                  </div>
                  {openSections.changes ? <ChevronUp className="h-5 w-5" data-id="element-760" data-mp="37wopyed9qdmo" /> : <ChevronDown className="h-5 w-5" data-id="element-761" data-mp="37um4m7n298oa" />}
                </button>
                {openSections.changes && <div className="p-6 bg-white" data-id="element-762" data-mp="3goidwlv4mqma">
                    <p className="text-slate-dark" data-id="element-763" data-mp="3gpsat7q8tsrc">
                      We may update this Privacy and Cookie Policy from time to
                      time to reflect changes in our services, practices, or
                      legal requirements. Any changes will be posted on our
                      website with an updated "Last Updated" date. We encourage
                      you to review this policy regularly.
                    </p>
                  </div>}
              </div>
              {/* Contact Us */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-764" data-mp="2q7uxsii6bl5w">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.contact ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('contact')} data-id="element-765" data-mp="1ske253dfpkwe">
                  <div className="flex items-center" data-id="element-766" data-mp="22h8zhixz1f93">
                    <Mail className="h-5 w-5 mr-2" data-id="element-767" data-mp="279ijkicenl3g" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-768" data-mp="27bd7m5iz3tbr">
                      10. Contact Us
                    </h2>
                  </div>
                  {openSections.contact ? <ChevronUp className="h-5 w-5" data-id="element-769" data-mp="1eqv2de0j4q7n" /> : <ChevronDown className="h-5 w-5" data-id="element-770" data-mp="3s2pi6hhs128p" />}
                </button>
                {openSections.contact && <div className="p-6 bg-white" data-id="element-771" data-mp="3j2kkx06ark89">
                    <p className="text-slate-dark mb-4" data-id="element-772" data-mp="3j19gcy5128g3">
                      For any questions about this Privacy and Cookie Policy,
                      our data protection practices, or to exercise your rights,
                      please contact us:
                    </p>
                    <p className="text-slate-dark" data-id="element-773" data-mp="34derjsu54o8a">
                      Email: enquiries@medicolegal-psychiatry.co.uk
                    </p>
                  </div>}
              </div>
              {/* Schedule */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-774" data-mp="2vet8lwkrb76z">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.schedule ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('schedule')} data-id="element-775" data-mp="2qckgt3jmeb6g">
                  <div className="flex items-center" data-id="element-776" data-mp="1qc771lpiyqbn">
                    <FileText className="h-5 w-5 mr-2" data-id="element-777" data-mp="1qdqpniynldgl" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-778" data-mp="24a44ekbsz0rd">
                      Schedule 1: Processing, Personal Data, and Data Subjects
                    </h2>
                  </div>
                  {openSections.schedule ? <ChevronUp className="h-5 w-5" data-id="element-779" data-mp="1bkf1g1fj8qn4" /> : <ChevronDown className="h-5 w-5" data-id="element-780" data-mp="17mhgjassoqpj" />}
                </button>
                {openSections.schedule && <div className="p-6 bg-white" data-id="element-781" data-mp="1glurk7wkhncd">
                    <p className="text-slate-dark mb-4" data-id="element-782" data-mp="1gk1jco6b2v29">
                      This schedule provides further detail on the data
                      processing activities undertaken by Medicolegal
                      Psychiatry.
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-783" data-mp="2zjqzzng7q433">
                      1. Processing by Medicolegal Psychiatry (as Data
                      Processor)
                    </h3>
                    <p className="text-slate-dark mb-4" data-id="element-784" data-mp="2qk47jr5go8m6">
                      Purpose of Processing: To provide medico-legal services,
                      including assessments, expert opinion reports, and court
                      testimony, as instructed by you (the Data Controller).
                      This involves processing client personal data provided by
                      you.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-785" data-mp="3990utb2fy0uw">
                      Scope: Processing client personal data necessary to
                      conduct assessments, arrange visits, communicate with
                      clients, and produce expert reports.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-786" data-mp="22u9up9l56k1x">
                      Duration: For the duration of the contract and as long as
                      data retention is required by law or professional
                      guidelines.
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-787" data-mp="19zlj4j6alery">
                      2. Types of Personal Data Processed (relating to Clients)
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-788" data-mp="14yebck45gfla">
                      <li className="mb-2" data-id="element-789" data-mp="102z07zxj0xsk">
                        Contact Details: Name, address, phone number, email.
                      </li>
                      <li className="mb-2" data-id="element-790" data-mp="r0fzbldawv8y">
                        Special Categories of Personal Data: Medical records,
                        mental health assessments, information about conditions,
                        reports on lifestyle, family history, legal case
                        details, and relevant historical data.
                      </li>
                      <li className="mb-2" data-id="element-791" data-mp="2ggfbzcg4p2tc">
                        Identification Data: Photographic identification, where
                        provided.
                      </li>
                      <li data-id="element-792" data-mp="2swotrn8yigco">
                        Assigned Reference Numbers: Unique identifiers assigned
                        to clients.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-793" data-mp="32mrobrvq82d4">
                      3. Categories of Data Subjects
                    </h3>
                    <ul className="list-disc pl-6 text-slate-dark" data-id="element-794" data-mp="29z5ise2xunnm">
                      <li className="mb-2" data-id="element-795" data-mp="2a1hy0jhbajwz">
                        The Subject: The individual undergoing assessment.
                      </li>
                      <li data-id="element-796" data-mp="2j0ljtv7v5df6">
                        The Instructing Party's Representatives: Case handlers,
                        solicitors, or other personnel from the instructing firm
                        who interact with us.
                      </li>
                    </ul>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}